import { IComponentNode } from 'app/app/editor/index.functions';
import { useAppBuilderContext } from 'app/app/editor/context';
import React, { useEffect, useState } from 'react';
import useCustomSimplifiedForm from 'components/common/form/hooks/useCustomSimplifiedForm';
import { alpha, Box, Button, Input, Stack } from '@mui/material';

import ReactHookFormController from 'components/common/form/layout/ReactHookFormController';
import ReactSelect from 'components/common/react-select/ReactSelect';
import IconMi from 'components/common/icon/IconMi';
import { prepareFormElementProps } from 'components/common/form/formTools';
import { useBlendColors } from 'hooks/useDesign';
import { useInstallFont } from 'hooks/useFont';
import { getFontOptions } from 'app/app/editor/tabs/settings/style-editor/FontField';
import { useDispatch, useSelector } from 'react-redux';
import { components } from 'react-select';

const variableTypeOptionsList: any = [
    {
        label: 'Color',
        value: 'color',
    },
    {
        label: 'Font',
        value: 'font',
    },
];

interface ManageVariableFormProps {
    availableFonts: any[];
    editIndex: number | null;
    onCancel: () => void;
}

const updateUsageStyleVariable = (
    variableName: string,
    element: any,
    action: {
        type: string;
        value?: string;
    },
    sendMessage?: (params: any) => void,
) => {
    const cssVarName = `var(--${variableName})`;

    //check properties and modify
    Object.keys(element?.params?.layout ?? {}).forEach((cssPropKey) => {
        if (element.params.layout[cssPropKey] === cssVarName) {
            switch (action.type) {
                case 'remove':
                    delete element.params.layout[cssPropKey];
                    break;
                case 'changeName':
                    element.params.layout[cssPropKey] = `var(--${action.value})`;
                    if (sendMessage) {
                        sendMessage({
                            name: 'updateComponent',
                            data: { uid: element.params.uid, updateType: 'style' },
                            broadcast: false,
                        });
                    }
                    break;
            }
        }
    });

    //check children
    (element?.elements ?? []).forEach((element: any) => {
        updateUsageStyleVariable(variableName, element, action, sendMessage);
    });
};

const SelectedValueComponent = (props: any) => {
    let label = props.data.label;
    if (props.data.value != '') {
        const groupIndex = props.options.findIndex((group: any) => {
            return group?.options && group.options.find((item: any) => item.value == props.data.value);
        });
        if (groupIndex !== -1) label = props.options[groupIndex].label + ':  ' + label;
    }
    return (
        <components.SingleValue {...props}>
            <Box>{label}</Box>
        </components.SingleValue>
    );
};
const ManageVariableForm = (props: ManageVariableFormProps) => {
    const ctx = useAppBuilderContext();
    const isNew = props.editIndex === null;
    const [variableType, setVariableType] = useState<'color' | 'font'>('color');
    const [variableTitle, setVariableTitle] = useState<string>('');
    const [variableName, setVariableName] = useState<string>('');
    const [variableValue, setVariableValue] = useState<string>('');

    const elementProps = useCustomSimplifiedForm({
        colorField: '#222222',
    });
    const formValueColor = elementProps.form.hookFormWatch('colorField');

    useEffect(() => {
        setVariableValue(formValueColor);
    }, [formValueColor]);

    useEffect(() => {
        if (props.editIndex !== null) {
            const currentEl = ctx.page?.cssVariables?.[props.editIndex];
            if (currentEl) {
                setVariableTitle(currentEl.title ?? '');
                setVariableType(currentEl.type);
                setVariableName(currentEl.name);
                setVariableValue(currentEl.value);
                if (currentEl.type == 'color') elementProps.form.hookFormSetValue('colorField', currentEl.value);
            }
        }
    }, []);

    const handleChangeVariableType = (value: any) => {
        if (variableType != value) {
            setVariableValue('');
        }
        setVariableType(value);
    };

    const onCancel = () => {
        props.onCancel();
    };
    const onApply = () => {
        if (variableTitle == '') {
            alert("Variable Title can't be empty");
            return false;
        }
        if (variableName == '') {
            alert("Variable Name can't be empty");
            return false;
        }
        if (variableValue == '') {
            alert("Variable Value can't be empty");
            return false;
        }

        if (!Array.isArray(ctx.page?.cssVariables)) {
            ctx.page['cssVariables'] = [];
        }

        const isUniqueTitle = ctx.page.cssVariables.findIndex((variable, index) => {
            const isNotSame = isNew ? true : index != props.editIndex;
            return isNotSame && variable.title == variableTitle;
        });

        if (isUniqueTitle !== -1) {
            alert('Variable Title should be unique');
            return false;
        }

        const isUnique = ctx.page.cssVariables.findIndex((variable, index) => {
            const isNotSame = isNew ? true : index != props.editIndex;
            return isNotSame && variable.name == variableName;
        });

        if (isUnique !== -1) {
            alert('Variable Name should be unique');
            return false;
        }

        if (props.editIndex === null) {
            ctx.page.cssVariables.push({
                title: variableTitle,
                name: variableName,
                type: variableType,
                value: variableValue,
            });
        } else {
            if (ctx.page.cssVariables[props.editIndex].name != variableName) {
                updateUsageStyleVariable(
                    ctx.page.cssVariables[props.editIndex].name,
                    ctx.page.layout,
                    {
                        type: 'changeName',
                        value: variableName,
                    },
                    ctx.sendMessage,
                );
            }

            ctx.page.cssVariables[props.editIndex] = {
                title: variableTitle,
                name: variableName,
                type: variableType,
                value: variableValue,
            };
        }

        ctx.sendMessage({ name: 'updateCssVariables' });
        ctx.setPage(ctx.page, false);
        props.onCancel();
    };
    return (
        <Box>
            <Box sx={{ fontWeight: 700, pb: 1 }}>{isNew ? 'Add new' : 'Edit'} Style Variable</Box>
            <Stack spacing={1}>
                <Box>
                    <Box>Title</Box>
                    <Box>
                        <Input
                            fullWidth
                            value={variableTitle}
                            onChange={(e) => {
                                setVariableTitle(e.target.value);
                            }}
                            placeholder={''}
                        />
                    </Box>
                </Box>
                <Box>
                    <Box>Type</Box>
                    <Box
                        sx={{
                            '& .custom-select__control--is-disabled': {
                                backgroundColor: (theme) => useBlendColors(alpha(theme.palette.text.primary, 0.08)),

                                '& .custom-select__single-value, & .custom-select__dropdown-icon': {
                                    color: (theme) => alpha(theme.palette.text.primary, 0.64),
                                },
                            },
                        }}
                    >
                        <ReactSelect
                            data={variableTypeOptionsList}
                            value={[variableType]}
                            update={handleChangeVariableType}
                            disabled={!isNew}
                        />
                    </Box>
                </Box>
                <Box>
                    <Box>Name</Box>
                    <Box>
                        <Input
                            fullWidth
                            value={variableName}
                            onChange={(e) => {
                                setVariableName(e.target.value);
                            }}
                            placeholder={''}
                        />
                    </Box>
                </Box>
                <Box>
                    <Box>Value</Box>

                    {variableType == 'color' && (
                        <Box>
                            <ReactHookFormController
                                elementProps={prepareFormElementProps({
                                    ...elementProps,
                                    component: {
                                        component: 'ColorPicker',
                                        name: 'colorField',
                                    },
                                })}
                            />
                        </Box>
                    )}
                    {variableType == 'font' && (
                        <Box>
                            <ReactSelect
                                data={props.availableFonts}
                                value={[variableValue]}
                                update={(value: any) => {
                                    setVariableValue(value);
                                }}
                                overrideComponents={{
                                    SingleValue: SelectedValueComponent,
                                }}
                            />
                        </Box>
                    )}
                    {/*{(
                        <Box>
                            <Input
                                fullWidth
                                value={variableValue}
                                onChange={(e) => {
                                    setVariableValue(e.target.value);
                                }}
                                placeholder={''}
                            />
                        </Box>
                    )}*/}
                </Box>
                <Stack spacing={1.5} direction={'row'} sx={{ pt: 1, justifyContent: 'flex-end' }}>
                    <Button onClick={onApply}>{isNew ? 'Add' : 'Save'}</Button>
                    <Button variant={'outlined'} color="neutral" onClick={onCancel}>
                        {'Cancel'}
                    </Button>
                </Stack>
            </Stack>
        </Box>
    );
};

interface ListOfVariablesProps {
    onEdit: (index: number) => void;
    availableFonts: any[];
}

const ListOfVariables = (props: ListOfVariablesProps) => {
    const ctx = useAppBuilderContext();

    if (!(ctx.page?.cssVariables?.length > 0)) return <Box>There are no style variables</Box>;

    return (
        <Stack spacing={2}>
            {['color', 'font'].map((type) => {
                const variablesByType = ctx.page.cssVariables.filter((variable) => {
                    return variable.type == type;
                });
                if (variablesByType.length) {
                    return (
                        <Box key={type}>
                            <Box
                                sx={{
                                    fontWeight: 600,
                                    color: 'primary.main',
                                    borderBottom: (theme) => `1px solid ${alpha(theme.palette.primary.main, 1)}`,
                                    textTransform: 'capitalize',
                                }}
                            >
                                {type}
                            </Box>
                            <Stack>
                                {variablesByType.map((variableObj) => {
                                    return (
                                        <Stack
                                            key={variableObj.name}
                                            direction={'row'}
                                            alignItems={'flex-end'}
                                            sx={{
                                                borderBottom: (theme) =>
                                                    `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
                                                '&:hover': {
                                                    backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.04),
                                                },
                                                py: 0.5,
                                            }}
                                        >
                                            <Box flexGrow={1}>
                                                <Box>{variableObj.title}</Box>
                                                <Box
                                                    component={'span'}
                                                    sx={{
                                                        mr: 0.5,
                                                        color: (theme) => alpha(theme.palette.text.primary, 0.64),
                                                    }}
                                                >
                                                    {variableObj.name}:
                                                </Box>
                                                <Box
                                                    component={'span'}
                                                    sx={{ color: (theme) => alpha(theme.palette.primary.main, 0.9) }}
                                                >
                                                    {type == 'font' ? (
                                                        <>
                                                            {props.availableFonts
                                                                .map((item) => {
                                                                    if (item.options) {
                                                                        return item.options;
                                                                    } else {
                                                                        return item;
                                                                    }
                                                                })
                                                                .flat(1)
                                                                .find((font) => {
                                                                    return font.value == variableObj.value;
                                                                })?.label ?? variableObj.value}
                                                        </>
                                                    ) : (
                                                        <>{variableObj.value}</>
                                                    )}
                                                </Box>
                                            </Box>
                                            <Box
                                                sx={{
                                                    ml: 1,
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    width: '18px',
                                                    height: '18px',
                                                    flexShrink: 0,
                                                    '&:hover': { color: 'primary.main', cursor: 'pointer' },
                                                }}
                                                onClick={() => {
                                                    const currentIndex = ctx.page.cssVariables.findIndex((variable) => {
                                                        return variable.name === variableObj.name;
                                                    });
                                                    if (currentIndex !== -1) props.onEdit(currentIndex);
                                                }}
                                            >
                                                <IconMi icon={'edit-pencil'} />
                                            </Box>
                                            <Box
                                                sx={{
                                                    ml: 1,
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    width: '18px',
                                                    height: '18px',
                                                    flexShrink: 0,
                                                    '&:hover': { color: 'error.main', cursor: 'pointer' },
                                                }}
                                                onClick={() => {
                                                    const currentIndex = ctx.page.cssVariables.findIndex((variable) => {
                                                        return variable.name === variableObj.name;
                                                    });
                                                    if (currentIndex !== -1) {
                                                        if (
                                                            !confirm(
                                                                `Are you sure you want to remove "${variableObj.name}" variable?`,
                                                            )
                                                        ) {
                                                            return false;
                                                        }
                                                        updateUsageStyleVariable(variableObj.name, ctx.page.layout, {
                                                            type: 'remove',
                                                        });
                                                        ctx.page.cssVariables.splice(currentIndex, 1);
                                                        ctx.sendMessage({ name: 'updateCssVariables' });
                                                        ctx.setPage(ctx.page, false);
                                                    }
                                                }}
                                            >
                                                <IconMi icon={'times'} />
                                            </Box>
                                        </Stack>
                                    );
                                })}
                            </Stack>
                        </Box>
                    );
                }
            })}
        </Stack>
    );
};

export default function StyleVariablesEditor({ node }: { node: IComponentNode }) {
    const [editIndex, setEditIndex] = useState<number | null | false>(false);
    const [availableFonts, setAvailableFonts] = useState<any>([]);
    const userBrandThemeProperties = useSelector((state: any) => state.auth?.userInfo?.brandThemeProperties);
    const fontList = useSelector((state: any) => state.font.list);
    const dispatch = useDispatch();

    useEffect(() => {
        setAvailableFonts(
            getFontOptions({
                cssVariables: [],
                brandFonts: userBrandThemeProperties?.fonts ?? [],
                useInstallFont: useInstallFont,
                fontList: fontList,
                dispatch: dispatch,
                skipEmpty: true,
            }),
        );
    }, []);

    return (
        <Box>
            {editIndex === false && (
                <>
                    <Box sx={{ pb: 1.5 }}>
                        <Button
                            onClick={() => {
                                setEditIndex(null);
                            }}
                        >
                            {'Add Style Variable'}
                        </Button>
                    </Box>
                    <ListOfVariables
                        availableFonts={availableFonts}
                        onEdit={(index) => {
                            setEditIndex(index);
                        }}
                    />
                </>
            )}

            {editIndex !== false && (
                <ManageVariableForm
                    availableFonts={availableFonts}
                    editIndex={editIndex}
                    onCancel={() => {
                        setEditIndex(false);
                    }}
                />
            )}
        </Box>
    );
}
